import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { getPostBySlug } from '../utils/blogUtils';
import { useLanguage } from '../contexts/LanguageContext';

const BlogPostContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.1rem 2rem 6rem;
  font-family: 'Montserrat', sans-serif;
`;

const PostHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const PostTitle = styled.h1`
  font-size: 3rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PostDate = styled.p`
  font-size: 1rem;
  color: #888;
  text-align: center;
`;

const PostImage = styled.div`
  width: 100%;
  max-width: 800px;
  height: 400px;
  background-image: url(${props => props.image ? `/images/${props.image}` : '/images/placeholder.jpg'});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  margin: 0 auto 3rem;
  border-radius: 8px;
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;

const PostContent = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #666;
  max-width: 800px;
  margin: 0 auto;
  
  p {
    margin-bottom: 1.5rem;
  }
  
  h2 {
    font-size: 1.8rem;
    font-weight: 600;
    margin: 2rem 0 1rem;
    color: #333;
  }
  
  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 1.5rem 0 1rem;
    color: #333;
  }
  
  blockquote {
    border-left: 4px solid #000000;
    padding-left: 1rem;
    font-style: italic;
    margin: 1.5rem 0;
    color: #666;
  }
  
  a {
    color: #555555;
    text-decoration: underline;
    
    &:hover {
      color: #000000;
    }
  }
  
  ul, ol {
    margin: 1.5rem 0;
    padding-left: 2rem;
  }
  
  li {
    margin-bottom: 0.5rem;
  }
`;

const BlogPost = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    // Since getPostBySlug is now synchronous, we don't need async/await
    try {
      const fetchedPost = getPostBySlug(slug);
      
      if (fetchedPost) {
        setPost(fetchedPost);
      } else {
        setError('Post not found');
      }
    } catch (err) {
      console.error('Error fetching blog post:', err);
      setError('Error loading blog post');
    } finally {
      setLoading(false);
    }
  }, [slug]);
  
  if (loading) {
    return <BlogPostContainer>
      <div style={{ textAlign: 'center', padding: '4rem 0' }}>
        {language === 'de' ? 'Lädt...' : 'Loading...'}
      </div>
    </BlogPostContainer>;
  }
  
  if (error || !post) {
    return <BlogPostContainer>
      <div style={{ textAlign: 'center', padding: '4rem 0' }}>
        {error || (language === 'de' ? 'Beitrag nicht gefunden' : 'Post not found')}
      </div>
    </BlogPostContainer>;
  }
  
  // Get the correct language version
  const title = typeof post.title === 'object' ? post.title[language] : post.title;
  const content = typeof post.content === 'object' ? post.content[language] : post.content;
  
  return (
    <BlogPostContainer>
      <PostHeader>
        <PostTitle>{title}</PostTitle>
        <PostDate>{new Date(post.date).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</PostDate>
      </PostHeader>
      
      <PostImage image={post.image} />
      
      <PostContent>
        {content && content.split('\n\n').map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        ))}
      </PostContent>
    </BlogPostContainer>
  );
};

export default BlogPost; 